import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Award, CheckCircle, ArrowRight, Wrench, Home, Building2, MapPin, Calendar, Sparkles, Quote } from 'lucide-react';

const expertise = [
  'Plumbing',
  'Heating',
  'Gas Fitting'
];

const stats = [
  { value: '25+', label: 'Years Trade Experience' },
  { value: '2009', label: 'Serving Calgary Since' },
  { value: '100%', label: 'Owner-Operated' },
];

export const OwnerSection = () => {
  return (
    <section data-testid="owner-section" className="py-24 section-tint relative overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-14"
        >
          <span className="eyebrow mb-5">Meet the Owner</span>
          <h2 className="font-heading font-extrabold text-4xl sm:text-5xl text-[#0F172A] mt-5 mb-4 leading-tight">
            Local. Hands-on. <br className="hidden sm:block" />
            <span className="text-[#0284C7]">Doing the job properly.</span>
          </h2>
        </motion.div>

        {/* Main two-column horizontal block */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
          {/* Big Quote / Story Card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-7"
          >
            <div className="relative h-full bg-white rounded-lg border border-slate-200 shadow-xl shadow-slate-200/40 p-10 lg:p-14">
              <Quote className="absolute top-8 left-8 w-14 h-14 text-slate-300" strokeWidth={1} fill="currentColor" />
              <div className="relative">
                <p className="font-heading text-2xl lg:text-3xl text-[#0F172A] leading-snug mb-6">
                  &ldquo;After 25 years on the tools, you learn that the right way <span className="text-[#0284C7]">is the only way</span>. That&rsquo;s how every DNB job gets done.&rdquo;
                </p>
                <div className="space-y-4 text-slate-600 leading-relaxed">
                  <p>
                    DNB Plumbing, Heating &amp; Gas Fitting Inc. is owner-operated by <strong className="text-[#0F172A]">Mike Radinovic</strong>, a journeyman plumber &amp; gas fitter with over 25 years of hands-on trade experience.
                  </p>
                  <p>
                    Serving Calgary and the surrounding area since 2009, DNB has built its reputation on honest service, clean workmanship, and practical solutions &mdash; for residential and light commercial customers alike.
                  </p>
                </div>

                <div className="mt-8 flex flex-wrap gap-2">
                  {expertise.map((e) => (
                    <span key={e} className="px-3.5 py-1.5 bg-slate-100 text-[#0369A1] text-sm font-bold rounded-full">
                      {e}
                    </span>
                  ))}
                </div>

                <div className="mt-8 pt-7 border-t border-slate-100 flex items-center gap-4">
                  <div className="droplet-icon flex-shrink-0">
                    <Wrench className="w-6 h-6" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="font-heading font-extrabold text-[#0F172A] text-xl">
                      Mike Radinovic
                    </div>
                    <div className="text-[#0284C7] font-semibold text-sm">
                      Owner &amp; Lead Tradesman
                    </div>
                    <div className="text-slate-500 text-xs mt-0.5">
                      Journeyman Plumber &amp; Gas Fitter
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Stats + CTA column */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-5 flex flex-col gap-5"
          >
            {/* Stats grid (vertical) */}
            <div className="grid grid-cols-1 gap-4 flex-1">
              {stats.map((s, i) => (
                <div
                  key={s.label}
                  className={`relative dnb-card-soft flex items-center gap-5 ${
                    i === 0 ? 'lg:transform lg:translate-x-2' : ''
                  }`}
                >
                  <div className="droplet-icon flex-shrink-0">
                    <span className="font-heading font-extrabold text-base">{s.value}</span>
                  </div>
                  <div className="flex-1">
                    <div className="text-[#0F172A] font-heading font-bold text-lg leading-tight">
                      {s.value}
                    </div>
                    <div className="text-sm text-slate-500">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Service scope card */}
            <div className="dnb-card-soft">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-[#0284C7]" />
                <span className="font-heading font-bold text-[#0F172A]">Service Scope</span>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {[
                  { icon: Home, text: 'Residential' },
                  { icon: Building2, text: 'Light Commercial' },
                  { icon: Wrench, text: 'Service & Renovation' },
                ].map((s) => (
                  <div key={s.text} className="flex items-center gap-2.5 text-slate-700 text-sm">
                    <s.icon className="w-4 h-4 text-[#0284C7]" />
                    {s.text}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <Link
              to="/about"
              className="btn-primary text-center w-full"
            >
              Learn More About DNB →
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default OwnerSection;
